import { Sheet, Stack, Table, Typography } from '@mui/joy';
import { Trash } from '@phosphor-icons/react';
import { get } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Control, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, FormField } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IPartFinderReel } from 'partFinder/types';
import { useGetUoms } from 'parts/hooks';
import { IUom } from 'parts/types';
import { Color, Level, Size, Variant } from 'theme/enums';

import { IAddReelsToQuoteForm } from './types';
import { getReelNote } from './utils';

interface IProps {
  reels: IPartFinderReel[];
  onLinesCountChange: (count: number) => void;
}

const tableSx = {
  '& td': { verticalAlign: 'middle' },
  '& th:nth-of-type(2), & td:nth-of-type(2)': { width: 110 },
  '& th:nth-of-type(3), & td:nth-of-type(3)': { width: 120 },
  '& th:last-of-type, & td:last-of-type': { width: 110 },
};

// One row per picked reel: which part and which reel it is, and how much of it to quote.
export const ReelLinesTable = ({ reels, onLinesCountChange }: IProps) => {
  const { t } = useTranslation();

  const { control, errors } = useFormContext();

  const { fields, remove } = useFieldArray<IAddReelsToQuoteForm, 'lines', 'fieldId'>({
    control: control as unknown as Control<IAddReelsToQuoteForm>,
    name: 'lines',
    keyName: 'fieldId',
  });

  const { records: uoms } = useGetUoms({ filters: { bomDisplay: 1 } });

  const reelsByRowId = useMemo(
    () => new Map((reels ?? []).map((reel) => [reel.id, reel])),
    [reels]
  );

  useEffect(() => {
    onLinesCountChange(fields.length);
  }, [fields.length, onLinesCountChange]);

  return (
    <Sheet variant={Variant.OUTLINED} sx={{ borderRadius: 'sm', overflow: 'auto' }}>
      <Table size={Size.SM} sx={tableSx}>
        <thead>
          <tr>
            <th>{t('headerPartNumber')}</th>
            <th>{t('headerQty')}</th>
            <th>{t('headerUom')}</th>
            <th>{t('headerManufacturer')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {fields.map((field, index) => {
            const reel = reelsByRowId.get(field.reelRowId);
            const reelNote = getReelNote(reel);

            return (
              <tr key={field.fieldId}>
                <td>
                  <Stack>
                    <Typography level={Level.TITLE_SM}>
                      {reel?.partNumber ?? DEFAULT_PLACEHOLDER}
                    </Typography>
                    {reel?.description && (
                      <Typography level={Level.BODY_SM}>{reel.description}</Typography>
                    )}
                    {reelNote && (
                      <Typography level={Level.BODY_SM} color={Color.NEUTRAL}>
                        {reelNote}
                      </Typography>
                    )}
                  </Stack>
                </td>
                <td>
                  <FormField
                    name={`lines.${index}.quantity`}
                    type={InputType.NUMBER_INPUT}
                    inputProps={{
                      control,
                      error: get(errors, `lines.${index}.quantity`),
                      required: true,
                      slotProps: { input: { min: 0, thousandSeparator: true } },
                    }}
                  />
                </td>
                <td>
                  <FormField
                    name={`lines.${index}.uom`}
                    type={InputType.SELECT}
                    inputProps={{
                      control,
                      error: get(errors, `lines.${index}.uom`),
                      required: true,
                      placeholder: t('placeholderChooseUom'),
                      options: uoms ?? [],
                      getLabel: (option: IUom) => option?.name,
                      getValue: (option: IUom) => option?.name,
                    }}
                  />
                </td>
                <td>
                  <Typography level={Level.BODY_SM}>
                    {reel?.manufacturerName ?? DEFAULT_PLACEHOLDER}
                  </Typography>
                </td>
                <td>
                  <Button
                    text={t('buttonLabelDelete')}
                    color={Color.DANGER}
                    variant={Variant.SOFT}
                    startDecorator={<Trash />}
                    onClick={() => remove(index)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Sheet>
  );
};
